import { Injectable } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { switchMap, catchError, withLatestFrom, tap, mergeMap, map, of } from 'rxjs'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { AuthenticationEntity, ShortUrl } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.models'
import { AuthVerifyApiService } from './auth-verify-api.service'
import { HttpErrorResponse } from '@angular/common/http'
import { Action } from '@ngrx/store'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { SelectedItemFacade } from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.facade'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src'
import { GbVerifyDialogComponent } from 'libs/gb-front/auth/feature/verify/src/lib/gb-verify-dialog/gb-verify-dialog.component'
import { Router } from '@angular/router'
import { GbVerifyNotificationStatusService } from 'libs/gb-front/auth/feature/verify-notification/src'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src'
import * as AuctionActions from 'libs/gb-front/auction/data-access/src/lib/+state/auction.actions'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import * as ProfileItemActivitiesActions from 'libs/gb-front/item/data-access-profile-item-activities/src/lib/+state/profile-item-activities.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'

@Injectable()
export class AuthVerifyEffects {
  verify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verify),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.Verify)),
      mergeMap(([action, urlName]) =>
        this.authVerifyService.verify(urlName, action.payload).pipe(
          switchMap((authentication: AuthenticationEntity) => [
            AuthActions.verifySuccess({
              authentication: authentication,
              dialog: action.dialog,
              onSuccess: action.onSuccess,
            }),
          ]),
          catchError(async (error: HttpErrorResponse) => AuthActions.verifyFailure({ error })),
        ),
      ),
    ),
  )

  verifyToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyToken),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => {
        this.loaderService.showLoaderDialog(LoaderActionTypeEnum.Verify)
      }),
      mergeMap(([action, urlName]) => {
        const payload = {
          verification_token: action.token,
        }
        return this.authVerifyService.verify(urlName, payload)
      }),
      map((authentication: AuthenticationEntity) =>
        AuthActions.verifySuccess({
          authentication: authentication,
          dialog: false,
        }),
      ),
      catchError(async (error: HttpErrorResponse) => AuthActions.verifyFailure({ error })),
    ),
  )

  verifySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifySuccess),
      withLatestFrom(this.authFacade.isVolunteer$, this.selectedItemFacade.selectedItem$),
      switchMap(([action, isVolunteer, currentItem]) => {
        const actions: Action[] = []

        if (action.onSuccess) actions.push(action.onSuccess())

        if (isVolunteer) {
          const onSelectProfileActions = currentItem
            ? [ProfileItemActivitiesActions.loadProfileItemActivityVolunteer()]
            : []
          actions.push(
            SelectedProfileActions.setSelectedProfile({
              uuid: action.authentication.profile.uuid,
              onSelectProfileActions,
            }),
          )
        } else {
          actions.push(
            AuthActions.setAuthentication({
              authentication: action.authentication,
            }),
          )
        }
        return actions
      }),
    ),
  )

  closeVerifyDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifySuccess),
        map((action) => {
          if (action.dialog) this.gbFrontDialogService.closeDialog(GbVerifyDialogComponent, true)
        }),
      ),
    { dispatch: false },
  )

  hideVerifyLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifySuccess, AuthActions.verifyFailure),
        map(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.Verify)
        }),
      ),
    { dispatch: false },
  )

  verifyNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyNotification),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => {
        this.gbVerifyNotificationStatusService.nVPending()
        this.loaderService.showLoaderDialog(LoaderActionTypeEnum.VerifyNotification)
      }),
      mergeMap(([action, urlName]) =>
        this.authVerifyService.verifyNotification(urlName, action.token).pipe(
          map((authentication) => AuthActions.verifyNotificationSuccess({ authentication })),
          catchError(async (error: HttpErrorResponse) => AuthActions.verifyNotificationFailure({ error })),
        ),
      ),
    ),
  )

  hideVerifyNotificationLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyNotificationSuccess, AuthActions.verifyNotificationFailure),
        map(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.VerifyNotification)
        }),
      ),
    { dispatch: false },
  )

  verifyNotificationSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyNotificationSuccess),
      map((action) => {
        this.gbVerifyNotificationStatusService.nVSuccess()
        return AuthActions.loginSuccess({
          authentication: {
            token: action.authentication.token,
            profile: action.authentication.profile,
          },
        })
      }),
    ),
  )

  verifyNotificationErrorMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyNotificationFailure),
        map(() => {
          this.gbVerifyNotificationStatusService.nVError()
        }),
      ),
    { dispatch: false },
  )

  verifyShortCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyShortCode),
      mergeMap((action) =>
        this.authVerifyService.verifyShortCode(action.code).pipe(
          map((shortUrl: ShortUrl) =>
            AuthActions.verifyShortCodeSuccess({
              shortCode: shortUrl,
            }),
          ),
          catchError(async (error: HttpErrorResponse) => AuthActions.verifyShortCodeFailure({ error })),
        ),
      ),
    ),
  )

  verifyShortCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyShortCodeSuccess),
      mergeMap((action) => {
        const { token, actual_url } = action.shortCode
        const actions: Action[] = [
          AuthActions.navigateToShortCodeUrl({
            actualUrl: actual_url,
          }),
        ]

        if (token) {
          actions.push(AuthActions.updateToken({ token }))
          this.gbFrontCookieService.setTokenCookie(token)
          actions.push(
            AuthActions.loginWithShortLink({
              urlName: this.getUrlName(action.shortCode.actual_url),
            }),
          )
        }

        return actions
      }),
    ),
  )

  verifyShortCodeFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyShortCodeFailure),
      map(() => AuctionActions.redirectHomePage()),
    ),
  )

  showVerifyErrorNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyFailure, AuthActions.verifyNotificationFailure, AuthActions.verifyShortCodeFailure),
        map((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error) this.gbNotificationService.error(error)
        }),
      ),
    { dispatch: false },
  )

  navigateToShortCodeUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.navigateToShortCodeUrl),
        map((action) => {
          this.router.navigateByUrl(this.getRouterUrl(action.actualUrl))
        }),
      ),
    { dispatch: false },
  )

  sendVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.sendVerification),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.SendVerification)),
      mergeMap(([action, urlName]) =>
        this.authVerifyService.sendVerification(urlName, action.uuid, action.payload).pipe(
          map(() => AuthActions.sendVerificationSuccess()),
          catchError((error) => of(AuthActions.sendVerificationFailure({ error }))),
        ),
      ),
    ),
  )

  hideSendVerificationLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sendVerificationSuccess, AuthActions.sendVerificationFailure),
        tap(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.SendVerification)),
      ),
    { dispatch: false },
  )

  sendVerificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sendVerificationSuccess),
        tap(() => {
          this.gbNotificationService.success('AUTH.SEND_VERIFICATION.SUCCESS_MESSAGE')
        }),
      ),
    { dispatch: false },
  )

  sendVerificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sendVerificationFailure),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbNotificationService.error(error || 'AUTH.SEND_VERIFICATION.ERROR_MESSAGE')
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private authVerifyService: AuthVerifyApiService,
    private selectedItemFacade: SelectedItemFacade,
    private gbNotificationService: GbNotificationService,
    private gbVerifyNotificationStatusService: GbVerifyNotificationStatusService,
    private gbFrontDialogService: GbFrontDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbFrontCookieService: GbFrontCookieService,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private loaderService: GbLoaderService,
    private router: Router,
  ) {}

  private getRouterUrl(url: string) {
    let routerUrl = `${this.getUrlName(url)}/${this.getUrlFinalSegment(url)}`
    if (url.includes('forgot-password')) {
      if (url.endsWith('/')) {
        // remove trailing slash
        url = url.slice(0, -1)
      }
      const urlName = this.getUrlSegment(url, 4)
      routerUrl = urlName + url.split(urlName)[1]
    } else {
      if (this.getVerifyToken(url)) {
        routerUrl = `${routerUrl}/${this.getVerifyToken(url)}`
      }
    }

    return routerUrl
  }

  private getUrlSegment(url: string, segmentFromEnd: number): string {
    const split: string[] = url.split('/')
    const length: number = split.length
    return `${split[length - segmentFromEnd]}`
  }

  private getVerifyToken = (url: string): string => this.getUrlSegment(url, 1)

  private getUrlFinalSegment = (url: string): string => this.getUrlSegment(url, 2)

  private getUrlName = (url: string): string => this.getUrlSegment(url, 3)
}
